



import { useMutation, useQuery } from "@tanstack/react-query";
import moment from "moment";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { validateImageFileType } from "../../helper/helper";
import useDetails from "../../hooks/useDetails";
import {
  addLabelImage,
  getItems,
  getLabelDetails,
} from "../../services/services";
import { constant } from "../../utils/constants";
import { toastAlert } from "../../utils/SweetAlert";
import "./createLabel.css";
import DateInput from "./label-component/DateInput";
import ImageInput from "./label-component/ImageInput";
import NutritionTable from "./label-component/NutritionTable";
import QRBar from "./label-component/QRBar";
import TextField from "./label-component/TextField";
import { days, labelTypes } from "../../utils/labelConstants";
import * as htmlToImage from "html-to-image";


const CreateLabelPublic = () => {
  const labelRef = useRef(null);
  const imageRef = useRef();
  const childRef = useRef();
  const details = useDetails();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const id = searchParams.get("id");
  const { state } = useLocation();
  const navigate = useNavigate();
  const [item, setItem] = useState([]);

  const [previewCompany, setPreviewCompany] = useState("");
  const [scale, setScale] = useState(2);
  const [preview, setPreview] = useState(true);
  const [borderChecked, setBorderChecked] = useState(false);
  const [selectedElement, setSelectedElement] = useState("");
  const [subItem, setSubItem] = useState("");
  const [previewItem, setPreviewItem] = useState("");
  const [expirationDay, setExpirationDay] = useState("");
  const [canvasStyle, setCanvasStyle] = useState({
    border: `2px none #42b03a`,
  });

  // const [searchParams] = useSearchParams();
  // const searchParams = new URLSearchParams(window.location.search);
  const authToken = searchParams.get("auth");

  const [isRotated, setIsRotated] = useState(false);
  const queryName = searchParams.get("name") || state?.name || "2X2";

  let label = state || {
    _id: searchParams.get("id"),
    name: searchParams.get("name"),
    slug: searchParams.get("slug"),
    type: searchParams.get("type"),
    metaData: searchParams.get("metaData")
      ? JSON.parse(searchParams.get("metaData"))
      : null,
    createdAt: searchParams.get("createdAt"),
  };
  const itemId = searchParams.get("item_id");
  const companyDomain = searchParams.get("companyDomain");
  const queryAddress = searchParams.get("address") || "";
  const queryWidth = searchParams.get("width");
  const queryHeight = searchParams.get("height");
  const userName = searchParams.get("user_name");
  const previewShow = searchParams.get("preview_show") === "true";

  const [width] = useState(queryWidth || queryName?.split("X")?.[0] || 2);
  const [height] = useState(queryHeight || queryName?.split("X")?.[1] || 2);
  const isCircleLabel =
    searchParams.get("slug") === constant.LABEL_TYPE.circle_2;

  useEffect(() => {
    const fetchItem = async () => {
      if (itemId && companyDomain) {
        try {
          let params = {
            page: 1,
            limit: 1000,
            header: companyDomain,
            token: authToken,
          };
          const res = await getItems(params);
          const data = res?.data?.data?.items;
          const previewItem = data?.find((i) => i._id == itemId);

          if (previewItem) {
            // Prepare preview item
            const newPreviewItem = {
              value: previewItem?._id,
              label: previewItem?.en_name,
              data: previewItem,
            };
            setPreviewItem(newPreviewItem);

            // 🚀 Immediately set label data
            // setTimeout(() => {
            //   updateItemValues(newPreviewItem);
            // }, 0);
          }
        } catch (err) {
          console.error("Error fetching item details:", err);
        }
      }
    };
    fetchItem();
  }, [itemId, companyDomain]);

  useEffect(() => {
    if (!previewItem || !item?.length) return;

    updateItemValues(previewItem);
  }, [previewItem, item.length]);

  // const [{ isOver }, drop] = useDrop(
  //   () => ({
  //     accept: "ITEM",

  //     collect: (monitor) => ({
  //       isOver: !!monitor.isOver(),
  //       canDrop: !preview && !!monitor.canDrop(),
  //     }),
  //   }),
  //   [preview]
  // );

  const imageMutation = useMutation({
    mutationFn: async ({ formData, el }) => {
      const resp = await addLabelImage(formData, authToken);
      return { url: resp?.data?.data?.imageUrl, el };
    },
    onSuccess: async ({ url, el }) => {
      const newItems = await Promise.all(
        item.map(async (i) => {
          if (i.id === el.id) {
            return { ...i, src: url };
          }
          return i;
        })
      );

      setItem(newItems);
    },
  });

  const { data, isSuccess } = useQuery({
    queryKey: ["label-details", id],
    queryFn: async () => {
      if (!id) return;

      const resp = await getLabelDetails(id, authToken);
      const data = resp?.data?.data;

      if (data) {
        const items = data?.metaData?.item || [];

        const updatedItems = items.map((el) => {
          if (el.key === "user_name") {
            return { ...el, value: userName };
          }

          if (el.key === "address") {
            return { ...el, value: queryAddress };
          }

          return el;
        });

        setItem((prev) => {
          if (prev?.length) return prev;
          return updatedItems;
        });
        setIsRotated(!!(data?.rotation ?? data?.rotate));
        setCanvasStyle(data?.metaData?.canvasStyle);
        setBorderChecked(!data?.metaData?.canvasStyle?.border?.includes("none"));
      }

      return true;
    },
    enabled: !!id,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: false,
    staleTime: Infinity,
  });

  /************************Upload Image functionality****************/
  const handleImageChange = async (e, el) => {
    if (!validateImageFileType(e.target.files[0])) {
      toastAlert("error", t("errorMsg.fileFormat"));
      return;
    }

    const file = e.target.files[0];
    if (!file) {
      return;
    }
    let formData = new FormData();
    formData.append("file", file);
    imageMutation.mutate({ formData, el });
  };

  const handleRemoveObject = (id) => {
    const newItems = item.filter((i) => i.id !== id);
    setItem(newItems);
  };



  const renderComponent = (fieldType, props) => {
    switch (fieldType) {
      case labelTypes.image:
        return (
          <ImageInput
            handleRemoveObject={handleRemoveObject}
            handleImageChange={handleImageChange}
            {...props}
          />
        );
      case labelTypes.text_fields:
        return (
          <TextField
            {...props}
            setItem={setItem}
            selectedElement={selectedElement}
            handleRemoveObject={handleRemoveObject}
            setSelectedElement={setSelectedElement}
          />
        );
      case labelTypes.start_date_field:
      case labelTypes.end_date_field:
      case labelTypes.transitional_date:
        return (
          <DateInput
            {...props}
            handleRemoveObject={handleRemoveObject}
            setSelectedElement={setSelectedElement}
            setItem={setItem}
            selectedElement={selectedElement}
          />
        );
      case labelTypes.nutritional_fields:
        return (
          <NutritionTable
            {...props}
            handleRemoveObject={handleRemoveObject}
            setSelectedElement={setSelectedElement}
            setItem={setItem}
            preview={preview}
            selectedElement={selectedElement}
            subItem={subItem}
            setSubItem={setSubItem}
          />
        );
      case labelTypes.bar_code:
      case labelTypes.qr_code:
        return <QRBar handleRemoveObject={handleRemoveObject} {...props} />;
      default:
        return <div>Unrecognized type</div>;
    }
  };

  const updateValue = (id, newValue) => {
    setItem((prevItems) =>
      prevItems.map((item) =>
        item.id === id ? { ...item, value: newValue } : item
      )
    );
  };

  function updateItemValues(selectedItem) {
    if (!selectedItem?.value) {
      toastAlert("error", "Please select item to fetch details");
      return;
    }

    const previewData = selectedItem?.data || {};

    const {
      days: thawDays = 0,
      hours: thawHours = 0,
      mins: thawMins = 0,
    } = previewData.thawExpiration || {};
    const {
      days: expDays = 0,
      hours: expHours = 0,
      mins: expMins = 0,
    } = previewData.expirationTime || {};

    const startDate = new Date(); // Made time

    // ---- THAW DATE ----
    const thawDate = new Date(startDate);
    if (previewData?.eodThrawExpiration) {
      thawDate.setDate(thawDate.getDate() + thawDays);
      thawDate.setHours(23, 59, 59, 999); // EOD = end of that day
    } else {
      thawDate.setDate(thawDate.getDate() + thawDays);
      thawDate.setHours(thawDate.getHours() + thawHours);
      thawDate.setMinutes(thawDate.getMinutes() + thawMins);
    }

    // ---- EXPIRATION DATE (starts after thaw ends) ----
    const endDate = new Date(thawDate);
    const isZeroExpiration = expDays === 0 && expHours === 0 && expMins === 0;

    if (previewData?.eodExpiration) {
      // EOD discard: same day if 0, else N days later (starting from thaw end)
      const offset = isZeroExpiration ? 0 : expDays;
      endDate.setDate(endDate.getDate() + offset);
      endDate.setHours(23, 59, 59, 999);
    } else {
      endDate.setDate(endDate.getDate() + expDays);
      endDate.setHours(endDate.getHours() + expHours);
      endDate.setMinutes(endDate.getMinutes() + expMins);
    }

    setExpirationDay(moment(endDate)?.format("ddd"));

    const barCodeValue = previewData?.additionalInfo?.barCodeNumber;
    const barCodeImage = previewData?.additionalInfo?.barCodeImage;

    const formatPercentage = (val) => {
      const num = parseFloat(val);
      if (isNaN(num) || num < 0) return "";
      if (num === 0) return "0%";
      if (num > 0 && num < 1) return "<1%";
      return `${Math.round(num)}%`;
    };

    // ✅ FIX: Functional update
    setItem((prev) => {
      if (!prev?.length) {
        return prev; // don’t overwrite
      }

      return prev.map((field) => {
        const { key, childKey } = field;
        if (childKey && previewData[key]?.[childKey] !== undefined) {
          const value = previewData[key][childKey];
          if (childKey === "sellingPrice")
            return { ...field, value: `$${value}` };
          if (childKey === "netWeight") return { ...field, value: `${value}g` };
          return { ...field, value };
        }

        if (key === "nutritionalInfo") {
          return {
            ...field,
            subItem: field.subItem?.map((sub) => {
              const { key: subKey, childKey: subChildKey } = sub;

              if (
                subChildKey &&
                previewData[subKey]?.[subChildKey] !== undefined
              ) {
                const value = previewData[subKey][subChildKey];
                const floatValue = parseFloat(value);
                let unitValue = value;
                let pValue = "";
                let indent = false;

                switch (subChildKey) {
                  case "totalFat":
                    unitValue = `${value}g`;
                    const fatCal = parseFloat(
                      previewData?.nutritionalInfo?.caloriesFromFat || 0
                    );
                    const totalCal = parseFloat(
                      previewData?.nutritionalInfo?.calories || 0
                    );
                    pValue =
                      totalCal > 0
                        ? formatPercentage((fatCal / totalCal) * 100)
                        : "N/A";
                    break;

                  case "saturatedFat":
                    unitValue = `${value}g`;
                    pValue = formatPercentage((floatValue / 20) * 100);
                    indent = true;
                    break;

                  case "transFat":
                    unitValue = `${value}g`;
                    pValue = "";
                    indent = true;
                    break;

                  case "cholesterol":
                    unitValue = `${value}mg`;
                    pValue = formatPercentage((floatValue / 300) * 100);
                    break;

                  case "sodium":
                    unitValue = `${value}mg`;
                    pValue = formatPercentage((floatValue / 2300) * 100);
                    break;

                  case "carbohydrates":
                    unitValue = `${value}g`;
                    pValue = formatPercentage((floatValue / 275) * 100);
                    break;

                  case "dietaryFiber":
                    unitValue = `${value}g`;
                    pValue = formatPercentage((floatValue / 28) * 100);
                    indent = true;
                    break;

                  case "totalSugar":
                    unitValue = `${value}g`;
                    pValue = "";
                    indent = true;
                    break;

                  case "addedSugar": {
                    const roundedPercent = formatPercentage(
                      (floatValue / 50) * 100
                    );
                    unitValue = `${value}`;
                    pValue = roundedPercent;
                    indent = "double";
                    break;
                  }

                  case "protein":
                    unitValue = `${value}g`;
                    pValue = formatPercentage((floatValue / 50) * 100);
                    break;

                  case "calcium":
                    unitValue = `${value}mg`;
                    pValue = formatPercentage((floatValue / 1300) * 100);
                    break;

                  case "vitaminA":
                    unitValue = `${value}mcg`;
                    pValue = formatPercentage((floatValue / 900) * 100);
                    break;

                  case "vitaminC":
                    unitValue = `${value}mg`;
                    pValue = formatPercentage((floatValue / 90) * 100);
                    break;

                  case "vitaminD":
                    unitValue = `${value}mcg`;
                    pValue = formatPercentage((floatValue / 20) * 100);
                    break;

                  case "iron":
                    unitValue = `${value}mg`;
                    pValue = formatPercentage((floatValue / 18) * 100);
                    break;

                  case "potassium":
                    unitValue = `${value}mg`;
                    pValue = formatPercentage((floatValue / 4700) * 100);
                    break;

                  default:
                    unitValue = value;
                }

                return {
                  ...sub,
                  value: unitValue,
                  pValue,
                  indent,
                };
              }

              return sub;
            }),
          };
        }

        if (key === "address") return { ...field, value: queryAddress };
        if (key === "user_name")
          return { ...field, value: userName };
        if (key === "startDate") return { ...field, value: startDate };
        if (key === "print_sequence") return { ...field, value: "1 of 5" };

        if (key === "thawExpiration") {
          const showThaw =
            thawDays > 0 ||
            thawHours > 0 ||
            thawMins > 0 ||
            previewData?.eodThrawExpiration;
          return {
            ...field,
            value: showThaw ? thawDate : false,
            eod: !!previewData?.eodThrawExpiration,
          };
        }

        if (key === "expirationTime") {
          const showExpiration =
            expDays > 0 ||
            expHours > 0 ||
            expMins > 0 ||
            (expDays === 0 && previewData?.eodExpiration);
          return {
            ...field,
            value: showExpiration ? endDate : false,
            eod: !!previewData?.eodExpiration,
          };
        }

        if (key === "bar_code" || key === "bar_code_variable") {
          const validValue =
            barCodeValue &&
            String(barCodeValue).trim() !== "0" &&
            barCodeValue !== 0
              ? String(barCodeValue)
              : "";
          const validSrc =
            barCodeImage &&
            (barCodeImage.includes("https") ||
              barCodeImage.includes("http") ||
              barCodeImage.startsWith("data:image"))
              ? barCodeImage
              : "";
          return {
            ...field,
            value: validValue,
            ...(validSrc ? { src: validSrc } : {}),
          };
        }

        if (previewData[key] !== undefined) {
          return { ...field, value: previewData[key] };
        }

        return field;
      });
    });
  }
  const generateLabelImage = async () => {
    if (!labelRef.current) return;

    try {
      const node = labelRef.current;
      const originalTransform = node.style.transform;
      node.style.transform = "none";

      const dataUrl = await htmlToImage.toPng(node, {
        backgroundColor: "#ffffff",
        pixelRatio: 4,
        cacheBust: true,
      });

      node.style.transform = originalTransform;

      const link = document.createElement("a");
      link.download = `label-${Date.now()}.png`;
      link.href = dataUrl;
      link.click();
    } catch (err) {
      console.error("Image generation failed:", err);
    }
  };
  useEffect(() => {
    if (!previewShow) return;

    if (item?.length && labelRef.current) {
      const timer = setTimeout(() => {
        generateLabelImage();
      }, 800);

      return () => clearTimeout(timer);
    }
  }, [item, previewShow]);


  return (
    <div className="mainbox" style={{ marginTop: "25%" }}>
      <section>
        <div className="">
          <div className="">
            <div className="row">
              <div className="col-lg-3 pe-lg-0"></div>

              <div className=" p-lg-0">
                <div style={{ overflow: "scroll" }}>
                  <div
                    id="div_label"
                    className="feature_Editor_Child d-flex justify-content-center"
                    ref={imageRef}
                    style={{ height: `${height * scale + 0.5}in` }}
                  >
                    <div
                      ref={labelRef}
                      className="label_Preview_Child"
                      style={{
                        height: isRotated ? `${width}in` : `${height}in`,
                        width: isRotated ? `${height}in` : `${width}in`,
                        transform: `scale(${scale})`,
                        transformOrigin: "top",
                        ...canvasStyle,
                        position: "relative",
                        ...(isCircleLabel
                          ? { borderRadius: "50%", overflow: "hidden", }
                          : {}),
                      }}
                    >
                      {/* Left Temper Evident Strip */}
                      {!previewShow && <>
                        {(label?.slug == constant.LABEL_TYPE.temper_evident_2x4 ||
                          label.slug ==
                          constant.LABEL_TYPE.temper_evident_2x6) && (
                            <div
                              style={{
                                width: isRotated ? "100%" : "23px",
                                height: isRotated ? "23px" : "100%",
                                backgroundColor: "#ff0000",
                                color: "#ffffff",
                                display: "flex",
                                alignItems: isRotated ? "center" : "flex-start",
                                justifyContent: "space-between",
                                flexDirection: isRotated ? "row" : "column",
                                writingMode: isRotated
                                  ? "horizontal-tb"
                                  : "vertical-rl",
                                fontSize: "14px",
                                fontWeight: "bold",
                                padding: isRotated ? "0 15px" : "15px 0",
                                position: "absolute",
                                left: isRotated ? 0 : 0,
                                top: isRotated ? 0 : 0,
                                transform: isRotated ? "none" : "none",
                                zIndex: 1,
                              }}
                            >
                              <div>Seal Secure</div>
                            </div>
                          )}
                      </>}

                      {/* Day of Week Labels (if applicable) */}
                      {(label?.slug == constant.LABEL_TYPE.day_of_week_2x1 ||
                        label?.slug == constant.LABEL_TYPE.day_of_week_2x2) && (
                          <div>
                            <div
                              className="d-flex"
                              style={{
                                position: "absolute",
                                zIndex: 1,
                                left: "50%",
                                ...(label.slug == constant.LABEL_TYPE.day_of_week_2x1
                                  ? {
                                    top: "80%",
                                    height:"20px",
                                    transform: `translate(-50%, -50%) ${isRotated ? "rotate(90deg)" : ""}`,
                                  }
                                  : {
                                    top: "50%",
                                    transform: `translate(-50%, -50%) ${isRotated ? "rotate(90deg)" : ""}`,
                                  }),
                              }}
                            >
                          
                              {days.map((day, index) => {
                                const is2x2 = label?.slug === constant.LABEL_TYPE.day_of_week_2x2;

                                const isBlackDay =
                                  preview &&
                                  previewItem?.data?._id &&
                                  expirationDay != day?.value;

                                return (
                                  <div
                                    key={index}
                                    style={{
                                      padding: "0 2px",
                                      ...(isBlackDay && {
                                        backgroundColor: "#000",
                                        color: "#000",
                                      }),
                                    }}
                                  >
                                    <div
                                      style={{
                                        ...(!previewShow ? day.style : {}),
                                        width: "22px",
                                        ...(is2x2 && { height: "30px" }),

                                        fontSize: "13px",
                                        fontWeight: "bold",
                                        textAlign: "center",
                                        ...(isBlackDay && {
                                          backgroundColor: "#000",
                                          color: "#000",
                                        }),
                                      }}
                                    >
                                      {(!previewShow || isBlackDay) && day.label}
                                    </div>
                                  </div>
                                );
                              })}

                            </div>
                            {!previewShow && <div
                              style={{
                                color: "#007200",
                                fontWeight: "bold",
                                fontSize: "8px",
                                textAlign: "end",
                                zIndex: 1,
                                position: "absolute",
                                bottom: "-5px",
                              
                                right: "0",
                                transform: `translate(-15%, -20%) ${isRotated ? "rotate(90deg)" : ""
                                  }`,
                              }}
                            >
                              Prep Guardian
                            </div>}

                          </div>
                        )}


                      {/* Main Content Area */}
                      <div
                        style={{
                          width: "100%",
                          height: "100%",
                          position: "relative",
                        }}
                        ref={childRef}
                        className="child-container"
                      >
                        {item?.map((element) => {
                          const props = {
                            element,
                            preview,
                            handleChange: (newValue, id) =>
                              updateValue(id, newValue),
                          };
                          return (

                            <div
                              key={element.id}
                              style={{
                                position: "absolute",
                                top: element.position?.y,
                                left: element.position?.x,
                                width: element.styles?.width,
                                height: element.styles?.height,
                              }}
                            >
                              {renderComponent(element.type, props)}
                            </div>
                          );
                        })}
                      </div>

                      {/* Right Temper Evident Strip */}
                      {!previewShow && <>
                        {(label?.slug == constant.LABEL_TYPE.temper_evident_2x4 ||
                          label.slug ==
                          constant.LABEL_TYPE.temper_evident_2x6) && (
                            <div
                              style={{
                                width: isRotated ? "100%" : "23px",
                                height: isRotated ? "23px" : "100%",
                                backgroundColor: "#ff0000",
                                color: "#ffffff",
                                display: "flex",
                                padding: isRotated ? "0 15px" : "15px 0",
                                alignItems: isRotated ? "center" : "flex-start",
                                justifyContent: "space-between",
                                flexDirection: isRotated ? "row" : "column",
                                writingMode: isRotated
                                  ? "horizontal-tb"
                                  : "vertical-rl",
                                fontSize: "14px",
                                fontWeight: "bold",
                                position: "absolute",
                                right: isRotated ? 0 : 0,
                                bottom: isRotated ? 0 : "auto",
                                top: isRotated ? "auto" : 0,
                                transform: isRotated
                                  ? "rotate(180deg)"
                                  : "rotate(180deg)",
                                zIndex: 1,
                              }}
                            >
                              <div>Tamper Evident</div>
                            </div>
                          )}
                      </>}


                    </div>
                  </div>
                </div>
              </div>
              <div className="col-lg-3 ps-lg-0"></div>
              {/* <button onClick={generateLabelImage} className="btn btn-primary mb-3">
  Download Label Image
</button> */}

            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CreateLabelPublic;
